// src/lib/learn/lesson-content-check.ts
//
// Render checks a lesson must pass before it is stored — pure, no database.
//
// Two ways a generated lesson can look broken on the page while its JSON looks
// fine (QA, the Systems lesson, 2026-10-10):
//
//   1. CURRENCY OPENING MATH. Lesson markdown renders through remark-math, so a
//      bare `$` is a math delimiter. "tickets for $12 each and child tickets
//      for $8 each" pairs the two dollar signs into one math span, and KaTeX
//      sets "12 each and child tickets for" as italic math with the spaces
//      squeezed out. Money must be written `\$12` (an escaped dollar renders as
//      a plain "$"). The generator's prompt says so (lesson-generation.ts v4);
//      this is the check that the output obeyed.
//
//   2. A FIGURE THAT DOESN'T SHOW WHAT ITS CAPTION SAYS. A function-graph whose
//      caption says "Solving x+y=14 and x-y=6 gives the intersection point" but
//      whose params plot only one of the two lines. A caption that names
//      equations must have each of them plotted; one that talks about where
//      lines intersect must plot at least two.
//
// The scanner below mirrors how remark-math (micromark-extension-math) pairs
// dollar signs (see scanDollars; held to the real renderer by
// __tests__/lesson-content-check.integration.test.tsx). A span is "prose, not math" when it holds two
// ordinary words in a row, one of them a function word ("each and", "in
// revenue"), or when it starts like an amount of money followed by a word
// ("5 or", "12 each"). Genuine math — `$12a + 8c = 1480$`, `$a + c = total$`,
// `$rate \times time$` — passes.

import {
  evalFn,
  overlayCurves,
  type FnSpec,
  type FunctionGraphParams,
} from '@/lib/render/function-graph-model'

export type LessonCheckIssue = {
  /** Where: "explanation", "workedExamples[1].problem", "diagrams.fig2", … */
  field: string
  kind: 'currency-math' | 'bare-currency' | 'diagram-missing-equation' | 'diagram-missing-line'
  message: string
}

/** The lesson fields the page renders as markdown (with math). */
export type LessonMarkdownContent = {
  explanation?: string
  workedExamples?: Array<{ problem?: string; solution?: string }>
  pitfalls?: Array<{ explanation?: string }>
  keyTakeaways?: string[]
  flashcards?: Array<{ front?: string; back?: string }>
  diagrams?: Array<{ id?: string; type?: string; params?: unknown; caption?: string }>
}

/* ------------------------------------------------------------------ */
/*  1. Dollar signs                                                    */
/* ------------------------------------------------------------------ */

/** Blank out fenced code blocks (block-level, so they win outright), keeping offsets. */
function blankFences(md: string): string {
  const blank = (s: string) => s.replace(/[^\n]/g, ' ')
  return md.replace(/^(\s*)(`{3,}|~{3,})[^\n]*\n[\s\S]*?^\s*\2[^\n]*$/gm, blank)
}

const ASCII_PUNCT = /[!-/:-@[-`{-~]/

/** Length of the run of `ch` starting at i. */
function runAt(s: string, i: number, ch: string): number {
  let j = i
  while (j < s.length && s[j] === ch) j++
  return j - i
}

/** Index of the next run of exactly `size` `ch` at or after `from`, or -1. */
function closingRun(s: string, from: number, ch: string, size: number): number {
  for (let i = from; i < s.length; i++) {
    if (s[i] !== ch) continue
    const n = runAt(s, i, ch)
    if (n === size) return i
    i += n - 1
  }
  return -1
}

export type MathSpan = { start: number; end: number; content: string; size: number }

/**
 * The math spans remark-math would make of `md`, the literal (unpaired) dollar
 * signs left over, and the inline code spans. Offsets are into `md`.
 *
 * Left to right, like micromark: a backslash before punctuation escapes it; a
 * run of N backticks is code up to the next run of exactly N (its dollars are
 * text); a run of N dollars is math up to the next run of exactly N — and a
 * math span opened first swallows any backticks before its close, which is how
 * `"$1,480" at \`$12\`` turns into math. An opener with no closer is literal.
 * Nothing pairs across a blank line.
 */
export function scanDollars(md: string): {
  spans: MathSpan[]
  literal: number[]
  code: Array<{ start: number; end: number }>
} {
  const text = blankFences(md)
  const spans: MathSpan[] = []
  const literal: number[] = []
  const code: Array<{ start: number; end: number }> = []
  const paraRe = /[\s\S]+?(?:\n[ \t]*\n|$)/g
  for (const para of text.matchAll(paraRe)) {
    const base = para.index ?? 0
    const p = para[0]
    for (let i = 0; i < p.length; i++) {
      const ch = p[i]
      if (ch === '\\' && ASCII_PUNCT.test(p[i + 1] ?? '')) {
        i++
        continue
      }
      if (ch !== '`' && ch !== '$') continue
      const n = runAt(p, i, ch)
      const close = closingRun(p, i + n, ch, n)
      if (close === -1) {
        if (ch === '$') literal.push(base + i)
        i += n - 1
        continue
      }
      if (ch === '`') code.push({ start: base + i, end: base + close + n })
      else
        spans.push({
          start: base + i,
          end: base + close + n,
          content: md.slice(base + i + n, base + close),
          size: n,
        })
      i = close + n - 1
    }
  }
  return { spans, literal, code }
}

const FUNCTION_WORDS = new Set(
  (
    'the and for each of in at to is are was were per with from on by or an it that this ' +
    'his her their than then if so but as be has had have who which what when how all any ' +
    'one total each its not into over after before more less only also just'
  ).split(' '),
)

/** Why a math span's content is prose rather than math, or null when it is math. */
export function proseInMath(content: string): string | null {
  // \text{…} and friends are legitimately words inside math; commands are not words.
  const bare = content
    .replace(/\\(?:text|textbf|textit|mathrm|mathit|mathbf|operatorname|mbox)\s*\{[^}]*\}/g, ' ')
    .replace(/\\[A-Za-z]+/g, ' ')
  // On the raw content: "$72\pi$" is a number times π; "$5 or $" is money.
  if (/^\s*\d[\d,]*(?:\.\d+)?\s+(?:[A-Za-z]{2,}|$)/.test(content))
    return 'starts like an amount of money'
  const words = bare.split(/\s+/)
  for (let i = 0; i + 1 < words.length; i++) {
    const a = words[i].replace(/[.,;:!?'"’)]+$/, '')
    const b = words[i + 1].replace(/^[("'‘]+|[.,;:!?'"’)]+$/g, '')
    if (!/^[A-Za-z]{2,}$/.test(a) || !/^[A-Za-z]{2,}$/.test(b)) continue
    if (FUNCTION_WORDS.has(a.toLowerCase()) || FUNCTION_WORDS.has(b.toLowerCase()))
      return `holds the words "${a} ${b}"`
  }
  return null
}

function excerpt(s: string, n = 48): string {
  const one = s.replace(/\s+/g, ' ').trim()
  return one.length > n ? `${one.slice(0, n - 1)}…` : one
}

/** Dollar-sign problems in one markdown field. */
export function dollarIssues(field: string, md: string): LessonCheckIssue[] {
  if (!md || !md.includes('$')) return []
  const issues: LessonCheckIssue[] = []
  const { spans, literal } = scanDollars(md)
  for (const s of spans) {
    const why = proseInMath(s.content)
    if (why)
      issues.push({
        field,
        kind: 'currency-math',
        message: `"${excerpt(md.slice(s.start, s.end))}" renders as math (it ${why}); write money as \\$`,
      })
  }
  // An unpaired money sign renders as text today, but the next bare `$` added
  // anywhere in the paragraph pairs with it. The house style is `\$`.
  for (const at of literal) {
    if (/^\$\s?\d/.test(md.slice(at, at + 3)))
      issues.push({
        field,
        kind: 'bare-currency',
        message: `bare money sign "${excerpt(md.slice(at, at + 20), 20)}" — write it as \\$`,
      })
  }
  return issues
}

/* ------------------------------------------------------------------ */
/*  2. Figures and their captions                                      */
/* ------------------------------------------------------------------ */

type Line = { slope: number; intercept: number }

/** a·x + b·y + c (one side of an equation), or null when it isn't linear in x, y. */
function linearSide(src: string): { x: number; y: number; c: number } | null {
  const s = src.replace(/\s+/g, '').replace(/[−–]/g, '-').replace(/·|\*/g, '')
  if (!s || /[^0-9xy+\-./()]/.test(s) || /[()]/.test(s)) return null
  const terms = s.match(/[+-]?[^+-]+/g)
  if (!terms || terms.join('') !== s) return null
  const out = { x: 0, y: 0, c: 0 }
  for (const t of terms) {
    const m = t.match(/^([+-]?)(\d+(?:\.\d+)?(?:\/\d+(?:\.\d+)?)?)?([xy])?$/)
    if (!m || (!m[2] && !m[3])) return null
    const sign = m[1] === '-' ? -1 : 1
    let k = 1
    if (m[2]) {
      const [n, d] = m[2].split('/')
      k = Number(n) / (d ? Number(d) : 1)
    }
    if (!Number.isFinite(k)) return null
    if (m[3] === 'x') out.x += sign * k
    else if (m[3] === 'y') out.y += sign * k
    else out.c += sign * k
  }
  return out
}

/** The line an equation in x and y describes, or null (vertical, or not linear). */
export function parseLinearEquation(eq: string): Line | null {
  const parts = eq.split('=')
  if (parts.length !== 2) return null
  const l = linearSide(parts[0])
  const r = linearSide(parts[1])
  if (!l || !r) return null
  const a = l.x - r.x
  const b = l.y - r.y
  const c = r.c - l.c
  if (b === 0 || (a === 0 && b === 0)) return null
  return { slope: -a / b, intercept: c / b }
}

/** Equations in x and y written in a caption ("x+y=14", "y = −2x + 12"). */
export function captionEquations(caption: string): string[] {
  const s = caption
    .replace(/\$/g, ' ')
    .replace(/[−–]/g, '-')
    // "f(x) = 4x − 3" is the graph y = 4x − 3.
    .replace(/\b[a-z]\(x\)\s*=/g, 'y =')
  const re =
    /[-+]?\s*[\d./]*\s*[xy](?:\s*[-+]\s*[\d./]*\s*[xy]?)*\s*=\s*[-+]?\s*[\d./]*\s*[xy]?(?:\s*[-+]\s*[\d./]*\s*[xy]?)*/g
  const out: string[] = []
  for (const m of s.matchAll(re)) {
    const at = m.index ?? 0
    const raw = m[0]
    const lead = raw.length - raw.trimStart().length
    const before = s[at + lead - 1] ?? ' '
    const after = s[at + raw.trimEnd().length] ?? ' '
    // Part of something longer: "y = x² − 7x" is not "y = x", "f(x) = …" not "x = …".
    if (/[\w)^²³]/.test(before) || /[\w(^²³√]/.test(after)) continue
    const eq = raw
      .trim()
      .replace(/^[+]/, '')
      .replace(/[./]+$/, '')
    // Needs something on both sides, and y on at least one (x = 3 can't be graphed as y = f(x)).
    const [l, r] = eq.split('=')
    if (!l?.trim() || !r?.trim() || !/y/.test(eq)) continue
    out.push(eq)
  }
  return out
}

/**
 * Does the caption say two graphs meet? Crossing an AXIS ("crosses the
 * x-axis", "where the parabola crosses zero") is about one graph and doesn't
 * count; "where they intersect", "both lines", "a line crosses this parabola"
 * do.
 */
export function claimsIntersection(caption: string): boolean {
  const c = caption.replace(
    /\b(?:cross(?:es|ing)?|intersect(?:s|ion|ing)?|meets?)\s+(?:the\s+)?(?:[xy]-axis|axis|zero)\b/gi,
    ' ',
  )
  return (
    /\bintersect(?:s|ion|ing|ed)?\b/i.test(c) ||
    /\b(?:two|both)\s+(?:lines|graphs|curves|equations)\b/i.test(c) ||
    /\beach equation\b/i.test(c) ||
    /\b(?:lines?|they|graphs?|curves?)\s+(?:cross|crosses|meet|meets)\b/i.test(c)
  )
}

function plotted(params: FunctionGraphParams): FnSpec[] {
  return [params, ...overlayCurves(params)]
}

function sameLine(spec: FnSpec, line: Line): boolean {
  // Compared by value at a few x's, so a curve of another kind never matches.
  return [-3, 0, 2, 5].every(
    (x) => Math.abs(evalFn(spec, x) - (line.slope * x + line.intercept)) < 1e-6,
  )
}

/** Caption-vs-plot problems for one diagram (function-graph only). */
export function diagramIssues(d: {
  id?: string
  type?: string
  params?: unknown
  caption?: string
}): LessonCheckIssue[] {
  if (d.type !== 'function-graph' || !d.caption) return []
  const field = `diagrams.${d.id ?? '?'}`
  const params = (d.params && typeof d.params === 'object' ? d.params : {}) as FunctionGraphParams
  const specs = plotted(params)
  const issues: LessonCheckIssue[] = []

  for (const eq of captionEquations(d.caption)) {
    const line = parseLinearEquation(eq)
    // y = 0 is the x-axis, which every graph draws.
    if (!line || (line.slope === 0 && line.intercept === 0)) continue
    if (!specs.some((s) => sameLine(s, line)))
      issues.push({
        field,
        kind: 'diagram-missing-equation',
        message: `caption names ${eq} but the figure doesn't plot it`,
      })
  }
  if (claimsIntersection(d.caption) && specs.length < 2)
    issues.push({
      field,
      kind: 'diagram-missing-line',
      message: `caption describes lines meeting ("${excerpt(d.caption, 60)}") but the figure plots ${specs.length} line`,
    })
  return issues
}

/* ------------------------------------------------------------------ */
/*  The whole lesson                                                   */
/* ------------------------------------------------------------------ */

/** Every markdown field, as [field name, text]. */
export function markdownFields(c: LessonMarkdownContent): Array<[string, string]> {
  const out: Array<[string, string]> = []
  if (c.explanation) out.push(['explanation', c.explanation])
  c.workedExamples?.forEach((e, i) => {
    if (e.problem) out.push([`workedExamples[${i}].problem`, e.problem])
    if (e.solution) out.push([`workedExamples[${i}].solution`, e.solution])
  })
  c.pitfalls?.forEach((p, i) => {
    if (p.explanation) out.push([`pitfalls[${i}].explanation`, p.explanation])
  })
  c.keyTakeaways?.forEach((t, i) => out.push([`keyTakeaways[${i}]`, t]))
  c.flashcards?.forEach((f, i) => {
    if (f.front) out.push([`flashcards[${i}].front`, f.front])
    if (f.back) out.push([`flashcards[${i}].back`, f.back])
  })
  return out
}

/** Everything about this lesson that would render wrong. Empty = clean. */
export function lessonRenderIssues(c: LessonMarkdownContent): LessonCheckIssue[] {
  return [
    ...markdownFields(c).flatMap(([field, md]) => dollarIssues(field, md)),
    ...(c.diagrams ?? []).flatMap((d) => diagramIssues(d)),
  ]
}

/**
 * Escape money in markdown: every bare `$` that starts an amount (`$12`,
 * `$1,480`, `$0.50`) and is not part of a genuine math span becomes `\$`.
 * Genuine math (`$a + c = total$`) is left alone; a currency-led span that is
 * really prose has its opening money sign escaped, which un-pairs it.
 */
export function escapeCurrency(md: string): string {
  if (!md.includes('$')) return md
  let s = md
  const money = (at: number) => /^\$\s?\d/.test(s.slice(at))
  // One at a time: escaping a dollar changes how the rest pair up.
  for (let pass = 0; pass < 50; pass++) {
    const { spans, literal } = scanDollars(s)
    const candidates: number[] = [...literal.filter(money)]
    for (const sp of spans) {
      if (!proseInMath(sp.content)) continue
      if (money(sp.start)) candidates.push(sp.start)
      // A closer inside what is written as code (`$12`) is left to the code:
      // escaping the opener frees it.
      const closeAt = sp.end - sp.size
      if (money(closeAt) && s[closeAt - 1] !== '`') candidates.push(closeAt)
    }
    if (candidates.length === 0) break
    const pos = Math.min(...candidates)
    s = `${s.slice(0, pos)}\\${s.slice(pos)}`
  }
  return s
}
